import { api } from '../api'
import { BEREICHE, erlaubteBereiche } from '../bereiche'
import { zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import type { Benutzer, BereichId } from '../typen'
import Meldungen from './Meldungen'

/**
 * Wer welchen Bereich öffnen darf - je Benutzer ein Häkchen je Bereich. Nur für die Rolle Verwaltung
 * sichtbar (die Seite fragt die Rolle ab); das Backend prüft es noch einmal.
 *
 * Ein Zugang ohne jeden Bereich wäre ein Zugang, mit dem man sich anmelden und nichts tun kann - das
 * lässt die Karte nicht zu. Sperren geht weiter über `scripts/benutzer.py`.
 */
export default function Zugaenge() {
  const { daten, fehler: ladeFehler, setDaten } = useLaden(() => api.benutzerListe(), [])
  const { fehler, erfolg, arbeitet, setFehler, ausfuehren } = useAktion()

  function umschalten(b: Benutzer, bereich: BereichId) {
    const jetzt = erlaubteBereiche(b)
    const neu = jetzt.includes(bereich) ? jetzt.filter((x) => x !== bereich) : [...jetzt, bereich]
    if (neu.length === 0) {
      setFehler(`${b.anzeigename} braucht mindestens einen Bereich. Zum Sperren bitte scripts/benutzer.py nutzen.`)
      return
    }
    const geordnet = BEREICHE.map((x) => x.id).filter((id) => neu.includes(id))
    void ausfuehren(async () => {
      const gespeichert = await api.benutzerBereiche(b.name, geordnet)
      setDaten((alt) => (alt ?? []).map((x) => (x.name === gespeichert.name ? gespeichert : x)))
    }, `Gespeichert: ${b.anzeigename} darf jetzt ${geordnet.map((id) => BEREICHE.find((x) => x.id === id)!.titel).join(' und ')} öffnen.`)
  }

  return (
    <div className="karte">
      <p className="hinweis" style={{ marginTop: 0 }}>
        Ein Häkchen gibt den Bereich frei. Die Änderung gilt ab dem nächsten Klick des Benutzers.
      </p>
      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />
      <div className="tabellenrahmen">
        <table className="zugaenge-tabelle">
          <thead>
            <tr>
              <th>Benutzer</th>
              <th>Rolle</th>
              <th>Zuletzt angemeldet</th>
              {BEREICHE.map((b) => (
                <th key={b.id}>{b.titel}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(daten ?? []).map((b) => {
              const erlaubt = erlaubteBereiche(b)
              return (
                <tr key={b.name} className={b.aktiv ? undefined : 'leise'}>
                  <td>
                    {b.anzeigename} <span className="hinweis">({b.name})</span>
                    {!b.aktiv && <span className="hinweis"> - gesperrt</span>}
                  </td>
                  <td>{b.rolle === 'verwaltung' ? 'Verwaltung' : 'Disponent'}</td>
                  <td>{b.letzte_anmeldung ? zeitpunkt(b.letzte_anmeldung) : <span className="hinweis">noch nie</span>}</td>
                  {BEREICHE.map((x) => (
                    <td key={x.id}>
                      <label className="haekchen">
                        <input type="checkbox" checked={erlaubt.includes(x.id)} disabled={arbeitet} onChange={() => umschalten(b, x.id)} />{' '}
                        {erlaubt.includes(x.id) ? 'darf' : 'gesperrt'}
                      </label>
                    </td>
                  ))}
                </tr>
              )
            })}
            {daten !== null && daten.length === 0 && (
              <tr>
                <td colSpan={3 + BEREICHE.length} className="leer">
                  Keine Zugänge angelegt.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
